import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { FaFilter, FaArrowRight, FaBullhorn, FaTimes, FaCalendarDay, FaFolderOpen } from 'react-icons/fa';
import './News.css';

// Import the image directly for the src tag (Same method as Video Gallery)
import img from '../../assets/breadcrumb/news.jpg'; 

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const formatFullDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

// Formatter for card date (e.g., "MAR 15")
const formatCardDate = (dateString) => {
  const d = new Date(dateString);
  const month = d.toLocaleString('default', { month: 'short' }).toUpperCase();
  const day = d.getDate();
  return `${month} ${day}`;
};

function News() {
  const [newsData, setNewsData] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/news/public`);
        const data = await response.json();
        setNewsData(data);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const categories = ['All', ...new Set(newsData.map(item => item.category))];
  const filteredNews = activeFilter === 'All' ? newsData : newsData.filter(item => item.category === activeFilter);

  const handleReadMore = (e, newsItem) => {
    e.stopPropagation(); 
    setSelectedNews(newsItem);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') handleCloseModal(); };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = isModalOpen ? 'hidden' : 'auto';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  return (
    <>
    <Helmet>
      <title>College News & Announcements | SK College</title>
      <meta
        name="description"
        content="Stay updated with the latest news, announcements, campus events, and academic updates from SK College."
      />
      <meta
        name="keywords"
        content="SK College News, College Announcements, Campus Events, Academic Updates"
      />
      <link rel="canonical" href="https://skcollegemanaklao.com/news" />
    </Helmet>

    <div className="news-and-events-wrapper">
      
      {/* --- UPDATED HEADER SECTION (Matches Video Gallery) --- */}
      <div className="news-and-events-header-container">
        <img src={img} alt="College News" className="news-and-events-header-img" />
        <div className="news-and-events-header-overlay">
          <h1 className="news-and-events-header-title">College News & Announcements</h1>
          <div className="news-and-events-breadcrumb">
            <span className="news-and-events-bread-static">Home</span>
            <span className="news-and-events-sep">/</span>
            <span className="news-and-events-bread-active">College News</span>
          </div>
        </div>
      </div>

      <div className="news-and-events-container">
        
        {/* Filter Bar */}
        <div className="news-and-events-filter-bar">
          <div className="news-and-events-filter-label"><FaFilter /> <span>Filter News:</span></div>
          <div className="news-and-events-filter-buttons">
            {categories.map((cat) => (
              <button key={cat} className={`news-and-events-filter-btn ${activeFilter === cat ? 'active' : ''}`} onClick={() => setActiveFilter(cat)}>{cat}</button>
            ))}
          </div>
        </div>

        {/* News Grid */}
        <div className="news-and-events-grid">
          {loading ? (
             <div className="news-and-events-loading">Loading News...</div>
          ) : filteredNews.length > 0 ? (
            filteredNews.map((news) => (
              <div key={news._id} className="news-and-events-card" onClick={(e) => handleReadMore(e, news)}>
                <div className="news-card-image-wrapper">
                  <img src={`${API_BASE_URL}${news.image}`} alt={news.title} className="news-card-image" onError={(e) => e.target.src='https://via.placeholder.com/400x300?text=No+Image'}/>
                  <div className="news-card-overlay">
                    <span className="news-card-date">{formatCardDate(news.publishDate)}</span>
                    <div className="news-card-title-row">
                      <h3 className="news-card-title">{news.title}</h3>
                      <div className="news-card-arrow-circle">
                        <FaArrowRight className="news-card-arrow" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="news-and-events-no-data"><FaBullhorn /><p>No news items found.</p></div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedNews && (
        <div className="news-and-events-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}>
          <div className="news-and-events-modal-article">
            <div className="news-and-events-modal-header">
              <button className="news-and-events-modal-close-btn" onClick={handleCloseModal}><FaTimes /></button>
              <h2 className="news-and-events-modal-title">{selectedNews.title}</h2>
              <div className="news-and-events-modal-meta-row">
                <span className="news-and-events-modal-meta-text"><FaCalendarDay /> {formatFullDate(selectedNews.publishDate)}</span>
                <span className="news-and-events-modal-sep">|</span>
                <span className="news-and-events-modal-meta-text"><FaFolderOpen /> {selectedNews.category}</span>
              </div>
            </div>
            <div className="news-and-events-article-body">
              <img src={`${API_BASE_URL}${selectedNews.image}`} alt={selectedNews.title} className="news-and-events-article-float-img" onError={(e) => e.target.src='https://via.placeholder.com/400x300?text=No+Image'}/>
              <div className="news-and-events-article-text-content">{selectedNews.content}</div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default News;